import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';

import Modal from 'common/components/organisms/Modal';
import { machineCsvActions } from '../../machineCsv.actions';
import ImportUploadStep from './ImportUploadStep';
import ImportProblemsStep from './ImportProblemsStep';
import ImportPreviewStep from './ImportPreviewStep';
import ImportDoneStep from './ImportDoneStep';
import { editableHeaders, getImportStep } from './helpers';

const WIDTH_BY_STEP = {
  upload: '44rem',
  problems: '54rem',
  preview: '56rem',
  done: '44rem'
};

function ImportModal({
  close, openExport, importState, catalog, actions
}) {
  const { t } = useTranslation();
  const step = getImportStep(importState);

  // Start clean and end clean, and fetch the limits afresh on every open.
  useEffect(() => {
    actions.resetMachinesCsvImport();
    actions.getCsvCatalog();
    return () => actions.resetMachinesCsvImport();
  }, []);

  // Closing mid-apply would hide whether the import was saved.
  const closeUnlessApplying = useCallback(() => {
    if (!importState.applying) close();
  }, [importState.applying, close]);

  const handleFile = useCallback(file => actions.previewMachinesCsvImport(file), [actions]);
  const startOver = useCallback(() => actions.resetMachinesCsvImport(), [actions]);
  const handleApply = useCallback(
    () => actions.applyMachinesCsvImport(importState.preview.changes),
    [actions, importState.preview]
  );
  const handleOpenExport = useCallback(() => {
    close();
    openExport();
  }, [close, openExport]);

  let content;
  if (step === 'done') {
    content = <ImportDoneStep applied={importState.applied} onDone={close} />;
  } else if (step === 'problems') {
    const afterApply = Boolean(importState.applyProblems && importState.applyProblems.length);
    content = (
      <ImportProblemsStep
        problems={afterApply ? importState.applyProblems : importState.preview.problems}
        fileName={importState.fileName}
        afterApply={afterApply}
        onUploadAgain={startOver}
      />
    );
  } else if (step === 'preview') {
    content = (
      <ImportPreviewStep
        preview={importState.preview}
        fileName={importState.fileName}
        applying={importState.applying}
        onApply={handleApply}
        onChooseAnother={startOver}
      />
    );
  } else {
    content = (
      <ImportUploadStep
        checking={importState.checking}
        fileName={importState.fileName}
        fileError={importState.fileError}
        maxRows={catalog ? catalog.max_rows : undefined}
        maxBytes={catalog ? catalog.max_bytes : undefined}
        editableFields={catalog ? editableHeaders(catalog.columns) : undefined}
        onFile={handleFile}
        onOpenExport={handleOpenExport}
        onCancel={close}
      />
    );
  }

  return (
    <Modal
      title={t('assets.csv.import.title', { defaultValue: 'Import asset changes' })}
      width={WIDTH_BY_STEP[step]}
      padding="0"
      close={closeUnlessApplying}
    >
      {content}
    </Modal>
  );
}

ImportModal.propTypes = {
  close: PropTypes.func.isRequired,
  openExport: PropTypes.func.isRequired,
  importState: PropTypes.shape({
    fileName: PropTypes.string,
    checking: PropTypes.bool,
    fileError: PropTypes.object,
    preview: PropTypes.object,
    applying: PropTypes.bool,
    applyProblems: PropTypes.array,
    applied: PropTypes.object
  }).isRequired,
  catalog: PropTypes.shape({
    max_rows: PropTypes.number,
    max_bytes: PropTypes.number,
    columns: PropTypes.array
  }),
  actions: PropTypes.shape({
    getCsvCatalog: PropTypes.func.isRequired,
    previewMachinesCsvImport: PropTypes.func.isRequired,
    applyMachinesCsvImport: PropTypes.func.isRequired,
    resetMachinesCsvImport: PropTypes.func.isRequired
  }).isRequired
};

ImportModal.defaultProps = {
  catalog: null
};

const mapStateToProps = state => ({
  importState: state.machines.csv.importState,
  catalog: state.machines.csv.catalog.data
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(machineCsvActions, dispatch)
});

export { ImportModal };
export default connect(mapStateToProps, mapDispatchToProps)(ImportModal);
